import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { useCurrentProject } from 'src/providers/current-project.provider'
import { getFormattedDate } from 'src/utils/date-formatter.util'
import { ErrorQueryHandler } from 'src/components/error/error-query-handler.component'
import { HRCampaignService } from 'src/services/hr-campaign.service'
import { ECampaignType, EHRDelay, EHRDispatchStatus } from 'src/types/hr'
import { HrPageWrapper } from '../../../_components/hr-page-wrapper.component'
import { LocalizedLink } from 'src/components/navigation/localized-link.component'
import { KpiCard } from '../../_components/kpi-card.component'
import { CampaignHeader } from '../../_components/campaign-header.component'
import { PlanBlancCampaignDispatchesTable } from '../_components/plan-blanc-campaign-dispatches-table.component'
import { useLocalizedNavigate } from 'src/hooks/use-localized-navigate'
import { useHRCampaignSocket } from '../../_hooks/use-hr-campaign-socket.hook'

type TPlanBlancFilter = 'responded' | EHRDelay | EHRDispatchStatus.PENDING

export default function PlanBlancCampaignDetailPage() {
  const { t } = useTranslation()
  const { project } = useCurrentProject()
  const navigate = useLocalizedNavigate()
  const { campaignId: campaignIdParam } = useParams<{ campaignId: string }>()
  const campaignId = parseInt(campaignIdParam!, 10)

  useHRCampaignSocket(campaignId)
  const [activeFilter, setActiveFilter] = useState<TPlanBlancFilter | null>(null)

  const {
    isLoading: isLoadingCampaign,
    error: campaignError,
    data: campaign,
  } = useQuery({
    queryKey: ['hr-campaign', project.id, campaignId],
    queryFn: () => HRCampaignService.getCampaign(project.id, campaignId),
  })

  useEffect(() => {
    if (campaign && campaign.type !== ECampaignType.PLAN_BLANC) {
      navigate(`/${project.id}/hr/campaigns/alert/${campaignId}`, { replace: true })
    }
  }, [campaign, campaignId, project.id, navigate])

  const {
    isLoading: isLoadingDispatches,
    error: dispatchesError,
    data: dispatches,
  } = useQuery({
    queryKey: ['hr-campaign-dispatches', project.id, campaignId],
    queryFn: () => HRCampaignService.getDispatches(project.id, campaignId),
    enabled: !!campaign,
  })

  const kpis = useMemo(() => {
    if (!dispatches) return null
    return {
      sent: dispatches.length,
      responded: dispatches.filter((d) => d.status === EHRDispatchStatus.COMPLETED).length,
      MIN_30: dispatches.filter((d) => d.delay === EHRDelay.MIN_30).length,
      H1: dispatches.filter((d) => d.delay === EHRDelay.H1).length,
      H2: dispatches.filter((d) => d.delay === EHRDelay.H2).length,
      UNAVAILABLE: dispatches.filter((d) => d.delay === EHRDelay.UNAVAILABLE).length,
      PENDING: dispatches.filter((d) => d.status === EHRDispatchStatus.PENDING).length,
      PENDING_REVIEW: dispatches.filter((d) => d.delay === EHRDelay.PENDING_REVIEW).length,
    }
  }, [dispatches])

  const filteredDispatches = useMemo(() => {
    if (!dispatches || activeFilter === null) return dispatches ?? []
    if (activeFilter === 'responded')
      return dispatches.filter((d) => d.status === EHRDispatchStatus.COMPLETED)
    if (activeFilter === EHRDispatchStatus.PENDING)
      return dispatches.filter((d) => d.status === EHRDispatchStatus.PENDING)
    return dispatches.filter((d) => d.delay === activeFilter)
  }, [dispatches, activeFilter])

  const toggleFilter = (filter: TPlanBlancFilter) => {
    setActiveFilter((prev) => (prev === filter ? null : filter))
  }

  if (campaignError || dispatchesError) {
    return <ErrorQueryHandler error={campaignError || dispatchesError} />
  }

  return (
    <HrPageWrapper
      title={
        <span className="c-flex c-items-baseline c-gap-3">
          <LocalizedLink
            to={`/${project.id}/hr/campaigns`}
            className="c-text-sm c-font-normal c-font-sans c-text-grey-500 hover:c-text-primary"
          >
            ← {t('components.navigation.hr-campaigns')}
          </LocalizedLink>
          {campaign && (
            <>
              <span className="c-text-grey-300">/</span>
              <span>
                {t('pages.hr.campaigns.detail.breadcrumb-title', {
                  type: t('pages.hr.campaigns.type.PLAN_BLANC'),
                  date: getFormattedDate(campaign.sentAt),
                })}
              </span>
            </>
          )}
        </span>
      }
    >
      {isLoadingCampaign && <div className="loading" />}

      {campaign && (
        <div className="c-flex c-flex-col c-flex-1 c-min-h-0">
          <CampaignHeader campaign={campaign} />

          {isLoadingDispatches && <div className="loading" />}

          {dispatches && kpis && (
            <div className="c-grid c-grid-cols-8 c-gap-3 c-mb-6">
              <KpiCard
                label={t('pages.hr.campaigns.detail.kpis.sent')}
                value={kpis.sent}
                colorClass="c-text-grey-600"
                bgColorClass="c-bg-grey-100"
                onClick={() => setActiveFilter(null)}
                isActive={activeFilter === null}
              />
              <KpiCard
                label={t('pages.hr.campaigns.detail.kpis.responded')}
                value={kpis.responded}
                colorClass="c-text-blue-600"
                bgColorClass="c-bg-blue-200"
                onClick={() => toggleFilter('responded')}
                isActive={activeFilter === 'responded'}
              />
              <KpiCard
                label={t('pages.hr.campaigns.detail.kpis.delay-30min')}
                value={kpis.MIN_30}
                colorClass="c-text-green-600"
                bgColorClass="c-bg-green-100"
                onClick={() => toggleFilter(EHRDelay.MIN_30)}
                isActive={activeFilter === EHRDelay.MIN_30}
              />
              <KpiCard
                label={t('pages.hr.campaigns.detail.kpis.delay-1h')}
                value={kpis.H1}
                colorClass="c-text-yellow-600"
                bgColorClass="c-bg-yellow-100"
                onClick={() => toggleFilter(EHRDelay.H1)}
                isActive={activeFilter === EHRDelay.H1}
              />
              <KpiCard
                label={t('pages.hr.campaigns.detail.kpis.delay-2h')}
                value={kpis.H2}
                colorClass="c-text-orange-700"
                bgColorClass="c-bg-orange-200"
                onClick={() => toggleFilter(EHRDelay.H2)}
                isActive={activeFilter === EHRDelay.H2}
              />
              <KpiCard
                label={t('pages.hr.campaigns.detail.kpis.unavailable')}
                value={kpis.UNAVAILABLE}
                colorClass="c-text-red-600"
                bgColorClass="c-bg-red-100"
                onClick={() => toggleFilter(EHRDelay.UNAVAILABLE)}
                isActive={activeFilter === EHRDelay.UNAVAILABLE}
              />
              <KpiCard
                label={t('pages.hr.campaigns.detail.kpis.waiting')}
                value={kpis.PENDING}
                colorClass="c-text-grey-600"
                bgColorClass="c-bg-grey-100"
                onClick={() => toggleFilter(EHRDispatchStatus.PENDING)}
                isActive={activeFilter === EHRDispatchStatus.PENDING}
              />
              <KpiCard
                label={t('pages.hr.campaigns.detail.kpis.a-verifier')}
                value={kpis.PENDING_REVIEW}
                colorClass="c-text-orange-700"
                bgColorClass="c-bg-orange-200"
                onClick={() => toggleFilter(EHRDelay.PENDING_REVIEW)}
                isActive={activeFilter === EHRDelay.PENDING_REVIEW}
              />
            </div>
          )}

          <h2 className="c-font-semibold c-font-nexa c-mb-3">
            {t('pages.hr.campaigns.detail.dispatches-title')}
          </h2>

          <div className="c-flex c-flex-col c-flex-1 c-min-h-0">
            {dispatches && filteredDispatches.length === 0 && (
              <p className="c-text-grey-500">{t('pages.hr.campaigns.detail.dispatches-empty')}</p>
            )}
            {dispatches && filteredDispatches.length > 0 && (
              <PlanBlancCampaignDispatchesTable dispatches={filteredDispatches} campaignId={campaignId} />
            )}
          </div>
        </div>
      )}
    </HrPageWrapper>
  )
}
